import { useContext, useEffect } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, ModalContext, SectionDivider, SidebarModal, SidebarNoticeBox, Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import usePublicationBookingItems from '@hooks/usePublicationBookingItems'

import type { PublicationBookingItemModel } from '@models/types'

import PublicationBookingItem from './components/PublicationBookingItem'

const MODAL_KEY = 'ReviewRemovedPublicationBookingItemsModal'

export type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ReviewRemovedPublicationBookingItemsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    afterAction: showCreatePublicationOrderModal,
    publicationOrderId,
  } = modalPayload

  const publicationBookingItemsPayload = usePublicationBookingItems({
    filters: {
      publication_order_id: publicationOrderId,
      removed: 'true',
    },
  })
  const {
    hasFilteredPublicationBookingItems,
    filteredPublicationBookingItems,
  } = publicationBookingItemsPayload

  // If there are no more PublicationBookingItems then this modal closes
  // and CreatePublicationOrderModal opens
  useEffect(() => {
    if (!hasFilteredPublicationBookingItems){
      closeModal()
      showCreatePublicationOrderModal()
    }
  }, [hasFilteredPublicationBookingItems])

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Review" titleSecondLine="Removed Ads" />

      <SidebarModal.Content>
        <SidebarNoticeBox>
          <SidebarNoticeBox.Title>
            <FontAwesomeIcon color="orange" icon={faExclamationTriangle} />
            <Text color="orange" style={{ display: 'inline-block' }}>&nbsp;Notice</Text>
          </SidebarNoticeBox.Title>

          <SidebarNoticeBox.Paragraph>
            Bookings have been removed from the plan that were previously confirmed by the publication.<br /><br />
            If you do not want to run these ads you will need to cancel them.<br /><br />
            If you cancel a booking you will not be able to run it again for the current edition.
          </SidebarNoticeBox.Paragraph>
        </SidebarNoticeBox>

        <SectionDivider boxProps={{ marginBottom: 'none' }}>Bookings</SectionDivider>

        {filteredPublicationBookingItems.map((publicationBookingItem: PublicationBookingItemModel) => (
          <PublicationBookingItem key={publicationBookingItem.id} publicationBookingItem={publicationBookingItem} />
        ))}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => closeModal()}
          size="large"
        >
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ReviewRemovedPublicationBookingItemsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
